import React, { Ref, useCallback, useMemo } from "react";
import { noop } from "@inode/multi-table-explorer";
import * as d3 from "d3";
import { List, ListItem, styled, Theme, useTheme } from "@mui/material";
import { SxProps } from "@mui/system";
import { useResizeObserver } from "@iva/resize-component";
import { Datum, ResultRadarProps, Vector2 } from "./types";
import { H1 } from "../Common/headings";
import { parseColor } from "#/util/colors";
import { RgbaObject } from "hex-rgb";
import { alpha as alphaStyleFn } from "@mui/material/styles";
import _ from "lodash";
import { isPresent } from "#types";

// const ResultCircle = styled("circle")(({theme}) => ({
//     // fill: theme.palette.primary.main,
// }));
const HighlightCircle = styled("circle")(({ theme }) => ({
    fill: theme.palette.success.main,
}));
const popover: SxProps<Theme> = {
    pointerEvents: "none",
};
const paper: SxProps<Theme> = {
    padding: theme => theme.spacing(1),
};
const title: SxProps<Theme> = {
    fontSize: 14,
};
const RadarArc = styled("path")(({ theme }) => ({
    fill: theme.palette.grey[300],
}));

function DefaultTTComponent<T>(data: Datum<T[]>) {
    return (
        <>
            <H1>{data.id}</H1>
            <List>
                {data.content.map((datum, i) => (
                    <ListItem key={i}>{i}</ListItem>
                ))}
            </List>
        </>
    );
}

const xy2da = (centroid: Vector2, angle: number, radius: number): Vector2 => {
    return {
        x: centroid.x + Math.cos(deg2rad(angle)) * radius,
        y: centroid.y + Math.sin(deg2rad(angle)) * radius,
    };
};

function deg2rad(a: number) {
    return (a / 360) * 2 * Math.PI;
}

export function D3Radar<T>({
    data,
    tooltipComponent = DefaultTTComponent,
    onClick = noop,
    colorScale = (d: Datum<T[]>) =>
        d3.scaleLinear().domain([0, 1]).clamp(true)(0.5),
}: ResultRadarProps<T>): JSX.Element {
    const theme = useTheme();
    const anchorEl = React.useRef<SVGPathElement>(null);
    const [
        resizeRef,
        observedSize = {
            width: 1000,
            height: 1000,
        },
    ] = useResizeObserver();
    const size = Math.min(observedSize.width, observedSize.height);

    // const [ttTarget, setTtTarget] = React.useState<Datum<T[]> | undefined>(undefined);
    const onMouseOver = (data: Datum<T[]>) => {
        showItems(data);
    };
    // const onMouseLeave = () => {
    //     setTtTarget(undefined);
    // };
    // const open = Boolean(ttTarget && anchorEl.current);
    // const ttWrapper = (x?: Datum<T[]>) => {
    //     return x? tooltipComponent(x) : null;
    // };

    const center: Vector2 = useMemo(
        () => ({ x: 0.5 * size, y: 0.5 * size }),
        [size]
    );
    const distance = center.x;
    const alpha = -225;
    const beta = 45;

    const arc = d3.path();
    arc.moveTo(center.x, center.y);
    arc.arc(center.x, center.y, distance, deg2rad(alpha), deg2rad(beta));
    arc.closePath();

    const showItems = useCallback(
        (x: Datum<T[]>) => {
            // setTtTarget(x);
            onClick(x);
        },
        [onClick]
        // [onClick, setTtTarget]
    );

    const { radiusScale, angleScale } = React.useMemo(() => {
        const distanceExtent: [number, number] = d3.extent(
            data.map(x => x.radius)
        ) as [number, number];
        const distanceScale = d3
            .scaleLinear()
            .domain(distanceExtent)
            .range([distance, 0]);
        // const radiusScale = d3.scaleDivergingPow().domain(x_extent).range([0, radius]);

        // map y-value to angle on the chart
        const angleExtent: [number, number] = d3.extent(
            data.map(x => x.angle)
        ) as [number, number];
        // const angleScale = d3.scaleLinear().domain(y_extent).range([alpha, beta]);
        const angleScale = d3
            .scaleLinear()
            .domain(angleExtent)
            .range([alpha, beta]);

        return { radiusScale: distanceScale, angleScale };
    }, [data, distance, alpha, beta]);

    const circleFill = React.useCallback(
        (d: Datum<T[]>) => {
            const colorValue = colorScale(d);
            const low: RgbaObject = parseColor(theme.palette.grey.A400);
            const high: RgbaObject = parseColor(theme.palette.primary.main);
            const rscale = d3.scaleLinear().range([low.red, high.red]);
            const gscale = d3.scaleLinear().range([low.green, high.green]);
            const bscale = d3.scaleLinear().range([low.blue, high.blue]);
            const ascale = d3.scaleLinear().range([low.alpha, high.alpha]);

            return `rgba(${rscale(colorValue)},${gscale(colorValue)},${bscale(
                colorValue
            )},${ascale(colorValue)})`;
        },
        [colorScale, theme.palette.grey.A400, theme.palette.primary.main]
    );

    const renderedPoints = React.useMemo(() => {
        const dataMappedToXy: (Datum<T> & Vector2)[] = data.map(
            ({ angle, radius, ...rest }) => {
                return {
                    ...rest,
                    ...xy2da(center, angleScale(angle), radiusScale(radius)),
                };
            }
        );
        const uniquePositions: (Datum<T> & Vector2)[] = _.uniqWith(
            dataMappedToXy,
            (x, y) => x.x === y.x && x.y === y.y
        );
        const delaunay = d3.Delaunay.from(
            uniquePositions.map(x => {
                return [x.x, x.y];
            })
        );
        const voronoi = delaunay.voronoi([0, 0, size, size]);

        // attach voronoi element
        // (Omit<DatumAngularSpace<T> & DatumEuclideanSpace<T>, "id"| "conte"> & { voronoi: string, ids: Id[] })[]
        const renderData: (Datum<T[]> & Vector2 & { voronoi: string })[] =
            uniquePositions.map(({ id, x, y }, i) => {
                const theSet: (Datum<T> & Vector2)[] = dataMappedToXy.filter(
                    p => x === p.x && y === p.y
                );
                return {
                    voronoi: voronoi.renderCell(i),
                    x,
                    y,
                    highlighted: theSet
                        .map(x => x.highlighted)
                        .filter(isPresent)
                        .reduce((x, y) => x || y, false),
                    content: theSet.map(x => x.content),
                    id: "" + x + y,
                };
            });

        // const aggregate = renderData.map(d => d.content).reduce((a,c) => ([...a,...c]),[]);
        // console.log(uniquePositions.length, dataMappedToXy.length, renderData.length, aggregate.length, data.length);

        return (
            <g key={"points"}>
                {renderData.map((actualDatum, i) => {
                    return (
                        <g key={"" + actualDatum.x + actualDatum.y}>
                            <circle
                                cx={actualDatum.x}
                                cy={actualDatum.y}
                                r={3 + (actualDatum.highlighted ? 1 : 0) * 3}
                                key={"c" + i}
                                fill={circleFill(actualDatum)}
                            />
                            {/*<text x={actualDatum.x} y={actualDatum.y}>{actualDatum.id}</text>*/}
                            <path
                                key={"p" + i}
                                d={actualDatum.voronoi}
                                stroke={"transparent"}
                                fill={
                                    actualDatum.highlighted
                                        ? alphaStyleFn(
                                              theme.palette.primary.dark,
                                              theme.palette.action
                                                  .selectedOpacity
                                          )
                                        : "transparent"
                                }
                                onMouseEnter={() => {
                                    onMouseOver(actualDatum);
                                    // showItem(actualDatum)
                                }}
                                onClick={() => {
                                    onMouseOver(actualDatum);
                                    // showItem(actualDatum)
                                }}
                                // onMouseLeave={onMouseLeave}
                            />
                        </g>
                    );
                })}
            </g>
        );
    }, [
        data,
        size,
        angleScale,
        radiusScale,
        center,
        circleFill,
        onMouseOver,
        theme.palette.action.selectedOpacity,
        theme.palette.primary.dark,
    ]);

    // const mouseOverPoint = React.useMemo(() => {
    //     if (ttTarget) {
    //         const pos1 = pos(
    //             center,
    //             angleScale(ttTarget.y),
    //             radiusScale(ttTarget.x),
    //             ttTarget.id
    //         );
    //         console.log("added mouseoverpoint", ttTarget, pos1);
    //         return <g id={"mouseOverpoint"}><HighlightCircle cx={pos1.x} cy={pos1.y} r={9}/></g>;
    //     } else {
    //         console.log("removed mouseoverpoint");
    //         return <g  id={"mouseOverpoint"} />;
    //     }
    // }, [ttTarget, center, angleScale, radiusScale, pos]);

    return (
        <div ref={resizeRef as Ref<HTMLDivElement>}>
            <svg width={size} height={size}>
                <defs>
                    <filter
                        id="f1"
                        x="-40%"
                        y="-40%"
                        height="200%"
                        width="200%"
                    >
                        <feOffset
                            result="offOut"
                            in="SourceAlpha"
                            dx="2"
                            dy="2"
                        />
                        <feGaussianBlur
                            result="blurOut"
                            in="offOut"
                            stdDeviation="1"
                        />
                        <feBlend
                            in="SourceGraphic"
                            in2="blurOut"
                            mode="normal"
                        />
                    </filter>
                </defs>
                <g key={"bg"} id={"background-arc"}>
                    <RadarArc d={arc.toString()} ref={anchorEl} />
                </g>
                {/*{mouseOverPoint}*/}
                {renderedPoints}
            </svg>
            {/*<HoverPopover*/}
            {/*    id="mouse-over-popover"*/}
            {/*    // sx={{...paper, ...popover}}*/}
            {/*    open={open}*/}
            {/*    anchorEl={anchorEl.current}*/}
            {/*    anchorOrigin={{*/}
            {/*        vertical: "bottom",*/}
            {/*        horizontal: "center",*/}
            {/*    }}*/}
            {/*    transformOrigin={{*/}
            {/*        vertical: "center",*/}
            {/*        horizontal: "center",*/}
            {/*    }}*/}
            {/*    onClose={onMouseLeave}*/}
            {/*    disableRestoreFocus*/}
            {/*>*/}
            {/*    {ttWrapper(ttTarget)}*/}
            {/*</HoverPopover>*/}
        </div>
    );
}
